import { observable } from "@legendapp/state";
import { Session, User } from "@supabase/supabase-js";
import { StateLogger } from "../utils/logger";
import { SubscriptionInfo } from '../types/subscription';
import { bindBufferedLogIdentity } from '../utils/logBuffer';

// Logger para o estado global
const stateLogger = StateLogger.Global;

// Interfaces para os tipos de dados
export interface UserState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  error: string | null;
  existsInDatabase: boolean;
  isPasswordRecovery: boolean;
  checkUserDatabaseDone: boolean;
  directSignup: DirectSignupState;
}

export interface UserProfile {
  id: string;
  name?: string;
  email?: string;
  telefone?: string;
  photo_url?: string;
  photo_local_path?: string; // Caminho local para a imagem (usado quando offline)
  empresa_id?: string;
  user_type?: string;
  created_at: string;
}

export interface DirectSignupState {
  isBootstrapping: boolean;
  isRequired: boolean;
  awaitingAppReady: boolean;
  overlayMessage: string;
  overlayHint: string;
  lastError: string | null;
  lastCompletedAt: string | null;
}

// Interface para o estado de assinatura
export interface SubscriptionState extends SubscriptionInfo {}

// Interface para o estado da empresa
export interface CompanyState {
  data: CompanyProfile | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

// Interface para dados da empresa
export interface CompanyProfile {
  id: string;
  nome: string;
  cnpj?: string;
  email?: string;
  telefone?: string;
  endereco?: string;
  cep?: string;
  cidade?: string;
  uf?: string;
  logo_url?: string;
  logo_local_path?: string; // Caminho local para a logo (usado quando offline)
  created_at?: string;
  updated_at?: string;
  plano?: string;
  stripe_customer_id?: string;
  num_fotos?: number;
}

// Interface para o estado de rede
export interface NetworkState {
  isConnected: boolean;
  connectionType: string | null;
  lastChecked: Date | null;
}

// Interface para o estado do banco de dados
export interface DatabaseState {
  isInitialized: boolean;
  adapterType: 'sqlite' | 'dexie' | null;
  lastSyncDate: Date | null;
  error: string | null;
  pendingChanges: number;
}

// Interface para o estado de sincronização
export interface SyncState {
  isRunning: boolean;
  lastSyncDate: Date | null;
  lastError: string | null;
  lastStatus: 'success' | 'error' | 'idle' | 'partial';
  attempts: number;
  prerequisitesMet: boolean;
  progress: {
    stage: 'idle' | 'prerequisites' | 'push' | 'pull' | 'cleanup';
    stageProgress: number; // 0-100
    overallProgress: number; // 0-100
  };
  realtimeSubscriptions: {
    active: boolean;
    tables: string[];
    subscriptionIds: string[];
  };
  pending: {
    totals: {
      images: number;
      deletions: number;
      operations: number;
      total: number;
    };
    processed: {
      images: { processed: number; success: number; errors: number };
      deletions: { processed: number; success: number; errors: number };
      operations: { processed: number; success: number; errors: number };
    };
  };
}

// Interface para o estado da aplicação
export interface AppState {
  isReady: boolean;
  lastReadyTimestamp: Date | null;
}

export interface UIState {
  trialBanner: {
    dismissedForExternalId: string | null;
    dismissedAt: string | null;
  };
  onboarding: {
    firstChecklistDismissed: boolean;
    dismissedAt: string | null;
    companyTipDismissed: boolean;
    companyTipDismissedAt: string | null;
    profileTipDismissed: boolean;
    profileTipDismissedAt: string | null;
  };
  adminDashboard: {
    filters: {
      startDate: number | null;
      endDate: number | null;
      status: 'in_progress' | 'done' | null;
      templateId: string | null;
      page: number;
    } | null;
    openRows: string[];
  };
}

// Estado global observável
export const globalState = observable({
  user: {
    isLoading: true,
    isAuthenticated: false,
    user: null,
    session: null,
    profile: null,
    error: null,
    existsInDatabase: false,
    isPasswordRecovery: false,
    checkUserDatabaseDone: false,
    directSignup: {
      isBootstrapping: false,
      isRequired: false,
      awaitingAppReady: false,
      overlayMessage: 'Estamos configurando sua conta...',
      overlayHint: 'Na primeira preparação, isso pode levar cerca de 1 minuto. Não feche o aplicativo.',
      lastError: null,
      lastCompletedAt: null,
    },
  } as UserState,

  subscription: {
    id: '',
    empresa_id: '',
    externalId: '',
    status: 'free',
    planType: 'free',
    planName: '',
    planId: '',
    startDate: null,
    nextBillingDate: null,
    expiration: null,
    lastChecked: new Date().toISOString(),
    billingProvider: 'stripe',
    isManual: false,
    paymentMethod: 'unknown',
  } as SubscriptionState,

  company: {
    data: null,
    isLoading: false,
    error: null,
    lastUpdated: null,
  } as CompanyState,

  network: {
    isConnected: false,
    connectionType: null,
    lastChecked: null,
  } as NetworkState,

  database: {
    isInitialized: false,
    adapterType: null,
    lastSyncDate: null,
    error: null,
    pendingChanges: 0,
  } as DatabaseState,

  sync: {
    isRunning: false,
    lastSyncDate: null,
    lastError: null,
    lastStatus: 'idle',
    attempts: 0,
    prerequisitesMet: false,
    progress: {
      stage: 'idle',
      stageProgress: 0,
      overallProgress: 0,
    },
    realtimeSubscriptions: {
      active: false,
      tables: [],
      subscriptionIds: [],
    },
    pending: {
      totals: {
        images: 0,
        deletions: 0,
        operations: 0,
        total: 0,
      },
      processed: {
        images: { processed: 0, success: 0, errors: 0 },
        deletions: { processed: 0, success: 0, errors: 0 },
        operations: { processed: 0, success: 0, errors: 0 },
      },
    },
  } as SyncState,

  app: {
    isReady: false,
    lastReadyTimestamp: null,
  } as AppState,

  ui: {
    trialBanner: {
      dismissedForExternalId: null,
      dismissedAt: null,
    },
    onboarding: {
      firstChecklistDismissed: false,
      dismissedAt: null,
      companyTipDismissed: false,
      companyTipDismissedAt: null,
      profileTipDismissed: false,
      profileTipDismissedAt: null,
    },
    adminDashboard: {
      filters: null,
      openRows: [],
    },
  } as UIState,
});

// Ações para manipular o estado
export const userActions = {
  setSession: (session: Session | null) => {
    stateLogger.debug('Atualizando sessão', session ? { userId: session.user.id } : 'null');
    globalState.user.set({
      isLoading: false,
      isAuthenticated: !!session,
      user: session?.user || null,
      session: session,
      profile: globalState.user.profile.get(),
      error: null,
      existsInDatabase: globalState.user.existsInDatabase.get(),
      isPasswordRecovery: globalState.user.isPasswordRecovery.get(),
      checkUserDatabaseDone: globalState.user.checkUserDatabaseDone.get(),
      directSignup: globalState.user.directSignup.get(),
    });
  },

  setProfile: (profile: UserProfile | null) => {
    stateLogger.debug('Atualizando perfil do usuário', profile?.id || 'null');
    globalState.user.profile.set(profile);
  },

  setLoading: (isLoading: boolean) => {
    //stateLogger.debug(`Definindo loading: ${isLoading}`);
    globalState.user.isLoading.set(isLoading);
  },

  setError: (error: string | null) => {
    if (error) {
      stateLogger.error('Erro no estado do usuário:', error);
    }
    globalState.user.error.set(error);
  },

  setUserExistsInDatabase: (exists: boolean) => {
    stateLogger.debug(`Definindo existência do usuário no banco: ${exists}`);
    globalState.user.existsInDatabase.set(exists);
  },

  setPasswordRecoveryMode: (isRecovery: boolean) => {
    stateLogger.debug(`Definindo modo de recuperação de senha: ${isRecovery}`);
    globalState.user.isPasswordRecovery.set(isRecovery);
  },

  setSubscriptionStatus: (status: Partial<SubscriptionState>) => {
    const oldValue = globalState.subscription.get();
    StateLogger.Global.info(`[DEBUG-PAUSE] setSubscriptionStatus - Valor antigo: ${JSON.stringify(oldValue)}`);
    StateLogger.Global.info(`[DEBUG-PAUSE] setSubscriptionStatus - Novo valor: ${JSON.stringify(status)}`);
    globalState.subscription.set({
      ...oldValue,
      ...status,
    });
  },

  resetDatabaseCheck: () => {
    stateLogger.info('[signup-direct] Resetando flags de verificação de usuário para novo bootstrap');
    globalState.user.checkUserDatabaseDone.set(false);
    globalState.user.existsInDatabase.set(false);
  },

  setDirectSignupBootstrapState: (patch: Partial<DirectSignupState>) => {
    const current = globalState.user.directSignup.get();
    const next = {
      ...current,
      ...patch,
    };
    if (patch.isBootstrapping !== undefined) {
      stateLogger.info(`[STATE:GLOBAL] Definindo bootstrap em andamento: ${patch.isBootstrapping}`);
    }
    if (patch.awaitingAppReady !== undefined) {
      stateLogger.info(`[STATE:GLOBAL] awaitingAppReady: ${patch.awaitingAppReady}`);
    }
    if (patch.overlayMessage || patch.overlayHint) {
      stateLogger.info(
        `[STATE:GLOBAL] Overlay atualizado -> mensagem: "${patch.overlayMessage ?? current.overlayMessage}" | dica: "${patch.overlayHint ?? current.overlayHint}"`
      );
    }
    globalState.user.directSignup.set(next);
  },

  clearUser: () => {
    bindBufferedLogIdentity(null);
    stateLogger.debug('Limpando dados do usuário');
    globalState.user.set({
      isLoading: false,
      isAuthenticated: false,
      user: null,
      session: null,
      profile: null,
      error: null,
      existsInDatabase: false,
      isPasswordRecovery: false,
      checkUserDatabaseDone: false,
      directSignup: {
        isBootstrapping: false,
        isRequired: false,
        awaitingAppReady: false,
        overlayMessage: 'Estamos configurando sua conta...',
        overlayHint: 'Na primeira preparação, isso pode levar cerca de 1 minuto. Não feche o aplicativo.',
        lastError: null,
        lastCompletedAt: null,
      },
    });

    // Limpar também o estado de assinatura
    globalState.subscription.set({
      id: '',
      empresa_id: '',
      externalId: '',
      status: 'free',
      planType: 'free',
      planName: '',
      planId: '',
      startDate: null,
      nextBillingDate: null,
      expiration: null,
      lastChecked: new Date().toISOString(),
      billingProvider: 'stripe',
      isManual: false,
      paymentMethod: 'unknown',
    });
  },
};

// Ações para manipular o estado de rede
export const networkActions = {
  setConnectionState: (isConnected: boolean, connectionType: string | null = null) => {
    stateLogger.debug(`Atualizando estado de conexão: ${isConnected ? 'conectado' : 'desconectado'}${connectionType ? ` (${connectionType})` : ''}`);
    globalState.network.set({
      isConnected,
      connectionType,
      lastChecked: new Date(),
    });
  },

  updateLastChecked: () => {
    stateLogger.debug('Atualizando timestamp de última verificação de rede');
    globalState.network.lastChecked.set(new Date());
  },
};

// Ações para o banco de dados
export const databaseActions = {
  setInitialized: (initialized: boolean, adapterType: 'sqlite' | 'dexie' | null = null) => {
    stateLogger.debug(`Banco de dados ${initialized ? 'inicializado' : 'não inicializado'}${adapterType ? ` com adapter ${adapterType}` : ''}`);

    globalState.database.isInitialized.set(initialized);

    if (adapterType) {
      globalState.database.adapterType.set(adapterType);
    }
  },

  updateSyncDate: () => {
    //stateLogger.debug('Atualizando data de última sincronização');
    globalState.database.lastSyncDate.set(new Date());
  },

  setError: (error: string | null) => {
    if (error) {
      stateLogger.error('Erro no banco de dados:', error);
    }
    globalState.database.error.set(error);
  },

  updatePendingChanges: (count: number) => {
    stateLogger.debug(`Atualizando contagem de alterações pendentes: ${count}`);
    globalState.database.pendingChanges.set(count);
  },

  incrementPendingChanges: () => {
    const currentCount = globalState.database.pendingChanges.get();
    globalState.database.pendingChanges.set(currentCount + 1);
    stateLogger.info(`Incrementando alterações pendentes: ${currentCount} -> ${currentCount + 1}`);
  },

  decrementPendingChanges: () => {
    const currentCount = globalState.database.pendingChanges.get();
    if (currentCount > 0) {
      globalState.database.pendingChanges.set(currentCount - 1);
      stateLogger.debug(`Decrementando alterações pendentes: ${currentCount} -> ${currentCount - 1}`);
    }
  },
};

type ProgressSnapshot = {
  stage: SyncState['progress']['stage'];
  stageProgress: number;
  overallProgress: number;
};

const createProgressSnapshot = (): ProgressSnapshot => ({
  stage: globalState.sync.progress.stage.get(),
  stageProgress: globalState.sync.progress.stageProgress.get(),
  overallProgress: globalState.sync.progress.overallProgress.get(),
});

const clonePendingSnapshot = (snapshot: SyncState['pending']): SyncState['pending'] => ({
  totals: { ...snapshot.totals },
  processed: {
    images: { ...snapshot.processed.images },
    deletions: { ...snapshot.processed.deletions },
    operations: { ...snapshot.processed.operations },
  },
});

const arePendingSnapshotsEqual = (a: SyncState['pending'], b: SyncState['pending']): boolean => {
  return (
    a.totals.images === b.totals.images &&
    a.totals.deletions === b.totals.deletions &&
    a.totals.operations === b.totals.operations &&
    a.totals.total === b.totals.total &&
    a.processed.images.processed === b.processed.images.processed &&
    a.processed.images.success === b.processed.images.success &&
    a.processed.images.errors === b.processed.images.errors &&
    a.processed.deletions.processed === b.processed.deletions.processed &&
    a.processed.deletions.success === b.processed.deletions.success &&
    a.processed.deletions.errors === b.processed.deletions.errors &&
    a.processed.operations.processed === b.processed.operations.processed &&
    a.processed.operations.success === b.processed.operations.success &&
    a.processed.operations.errors === b.processed.operations.errors
  );
};

let lastSyncRunningState = globalState.sync.isRunning.get();
let lastSyncStatusValue = globalState.sync.lastStatus.get();
let lastProgressSnapshot: ProgressSnapshot = createProgressSnapshot();
let lastPendingSnapshot = clonePendingSnapshot(globalState.sync.pending.get());

// Ações para o sincronização
export const syncActions = {
  setSyncRunning: (isRunning: boolean) => {
    if (lastSyncRunningState === isRunning) {
      globalState.sync.isRunning.set(isRunning);
      return;
    }

    StateLogger.SyncStatus.debug(`Sincronização ${isRunning ? 'iniciada' : 'finalizada'}`);
    globalState.sync.isRunning.set(isRunning);
    lastSyncRunningState = isRunning;

    // Se não estiver mais rodando, reseta o progresso
    if (!isRunning) {
      stateLogger.info('Sincronização finalizada, resetando progresso');
      globalState.sync.progress.stage.set('idle');
      globalState.sync.progress.stageProgress.set(0);
      globalState.sync.progress.overallProgress.set(0);
      lastProgressSnapshot = {
        stage: 'idle',
        stageProgress: 0,
        overallProgress: 0,
      };

      const emptyPendingSnapshot: SyncState['pending'] = {
        totals: {
          images: 0,
          deletions: 0,
          operations: 0,
          total: 0,
        },
        processed: {
          images: { processed: 0, success: 0, errors: 0 },
          deletions: { processed: 0, success: 0, errors: 0 },
          operations: { processed: 0, success: 0, errors: 0 },
        },
      };

      globalState.sync.pending.set(emptyPendingSnapshot);
      lastPendingSnapshot = clonePendingSnapshot(emptyPendingSnapshot);
    }
  },

  setPrerequisitesMet: (met: boolean) => {
    StateLogger.SyncPrerequisites.debug(`Pré-requisitos de sincronização ${met ? 'atendidos' : 'não atendidos'}`);
    globalState.sync.prerequisitesMet.set(met);
  },

  updateSyncDate: () => {
    globalState.sync.lastSyncDate.set(new Date());
  },

  setSyncError: (error: string | null) => {
    if (error) {
      StateLogger.SyncError.error('Erro na sincronização:', error);
      globalState.sync.lastStatus.set('error');
      lastSyncStatusValue = 'error';
    }
    globalState.sync.lastError.set(error);
  },

  setSyncStatus: (status: 'success' | 'error' | 'idle' | 'partial') => {
    if (lastSyncStatusValue === status) {
      return;
    }

    if (status === 'success' || status === 'partial') {
      const now = new Date();
      StateLogger.SyncStatus.info(`Status de sincronização alterado para: ${status} (registrado em ${now.toISOString()})`);
      globalState.sync.lastSyncDate.set(now);
    } else {
      StateLogger.SyncStatus.info(`Status de sincronização alterado para: ${status}`);
    }

    globalState.sync.lastStatus.set(status);
    lastSyncStatusValue = status;
  },

  incrementAttempts: () => {
    const currentAttempts = globalState.sync.attempts.get();
    globalState.sync.attempts.set(currentAttempts + 1);
    StateLogger.SyncRetry.debug(`Incrementando tentativas de sincronização: ${currentAttempts} -> ${currentAttempts + 1}`);
  },

  resetAttempts: () => {
    StateLogger.SyncRetry.debug('Resetando contador de tentativas de sincronização');
    globalState.sync.attempts.set(0);
  },

  updateProgress: (stage: 'idle' | 'prerequisites' | 'push' | 'pull' | 'cleanup', stageProgress: number, overallProgress: number) => {
    const previous = lastProgressSnapshot;
    const stageCompleted = stageProgress === 100 && previous.stageProgress !== 100;
    const overallCompleted = overallProgress === 100 && previous.overallProgress !== 100;

    if (stageCompleted || overallCompleted) {
      const messageParts = [`Estágio: ${stage}`, `Progresso do estágio: ${stageProgress}%`, `Progresso geral: ${overallProgress}%`];
      StateLogger.SyncStatus.debug(`Progresso da sincronização - ${messageParts.join(', ')}`);
    }

    globalState.sync.progress.set({
      stage,
      stageProgress,
      overallProgress,
    });
    lastProgressSnapshot = { stage, stageProgress, overallProgress };
  },

  setPendingSnapshot: (snapshot: SyncState['pending']) => {
    if (arePendingSnapshotsEqual(lastPendingSnapshot, snapshot)) {
      return;
    }

    StateLogger.SyncStatus.debug(
      `Atualizando snapshot de pendências: totals=${JSON.stringify(snapshot.totals)}, processed=${JSON.stringify(snapshot.processed)}`
    );
    globalState.sync.pending.set(snapshot);
    lastPendingSnapshot = clonePendingSnapshot(snapshot);
  },

  updateRealtimeSubscriptions: (active: boolean, tables: string[] = [], subscriptionIds: string[] = []) => {
    StateLogger.RealtimeMain.debug(`Atualizando estado das inscrições realtime: ${active ? 'ativas' : 'inativas'}, Tabelas: ${tables.join(', ')}`);
    globalState.sync.realtimeSubscriptions.set({
      active,
      tables,
      subscriptionIds,
    });
  },

  addRealtimeSubscription: (table: string, subscriptionId: string) => {
    const currentTables = globalState.sync.realtimeSubscriptions.tables.get();
    const currentIds = globalState.sync.realtimeSubscriptions.subscriptionIds.get();

    if (!currentTables.includes(table)) {
      currentTables.push(table);
    }

    if (!currentIds.includes(subscriptionId)) {
      currentIds.push(subscriptionId);
    }

    StateLogger.RealtimeSetup.debug(`Adicionada inscrição realtime: ${table} (ID: ${subscriptionId})`);

    globalState.sync.realtimeSubscriptions.tables.set([...currentTables]);
    globalState.sync.realtimeSubscriptions.subscriptionIds.set([...currentIds]);
    globalState.sync.realtimeSubscriptions.active.set(true);
  },

  clearRealtimeSubscriptions: () => {
    StateLogger.RealtimeCleanup.debug('Limpando todas as inscrições realtime');
    globalState.sync.realtimeSubscriptions.set({
      active: false,
      tables: [],
      subscriptionIds: [],
    });
  },
};

// Ações para o estado da aplicação
export const appActions = {
  setReady: (isReady: boolean) => {
    StateLogger.AppActionSetReady.debug(`Definindo estado da aplicação: ${isReady ? 'pronto' : 'não pronto'}`);
    globalState.app.isReady.set(isReady);

    if (isReady) {
      globalState.app.lastReadyTimestamp.set(new Date());
      StateLogger.AppActionSetReady.info(`Aplicação pronta para uso em: ${new Date().toISOString()}`);
    }
  }
};

// Ações de UI
export const uiActions = {
  dismissTrialBanner: (externalId: string | null | undefined) => {
    if (!externalId) {
      return;
    }
    globalState.ui.trialBanner.set({
      dismissedForExternalId: externalId,
      dismissedAt: new Date().toISOString(),
    });
  },
  resetTrialBanner: () => {
    globalState.ui.trialBanner.set({
      dismissedForExternalId: null,
      dismissedAt: null,
    });
  },
  dismissFirstChecklistOnboarding: () => {
    const current = globalState.ui.onboarding.get();
    globalState.ui.onboarding.set({
      ...current,
      firstChecklistDismissed: true,
      dismissedAt: new Date().toISOString(),
    });
  },
  resetFirstChecklistOnboarding: () => {
    globalState.ui.onboarding.set({
      firstChecklistDismissed: false,
      dismissedAt: null,
      companyTipDismissed: false,
      companyTipDismissedAt: null,
      profileTipDismissed: false,
      profileTipDismissedAt: null,
    });
  },
  dismissCompanyBrandingTip: () => {
    const current = globalState.ui.onboarding.get();
    if (current.companyTipDismissed) {
      return;
    }
    globalState.ui.onboarding.set({
      ...current,
      companyTipDismissed: true,
      companyTipDismissedAt: new Date().toISOString(),
    });
  },
  resetCompanyBrandingTip: () => {
    const current = globalState.ui.onboarding.get();
    globalState.ui.onboarding.set({
      ...current,
      companyTipDismissed: false,
      companyTipDismissedAt: null,
    });
  },
  dismissProfileBrandingTip: () => {
    const current = globalState.ui.onboarding.get();
    if (current.profileTipDismissed) return;
    globalState.ui.onboarding.set({
      ...current,
      profileTipDismissed: true,
      profileTipDismissedAt: new Date().toISOString(),
    });
  },
  resetProfileBrandingTip: () => {
    const current = globalState.ui.onboarding.get();
    globalState.ui.onboarding.set({
      ...current,
      profileTipDismissed: false,
      profileTipDismissedAt: null,
    });
  },
};

// Ações para manipular o estado da empresa
export const companyActions = {
  setCompanyData: (company: CompanyProfile | null) => {
    stateLogger.debug('Atualizando dados da empresa', company?.id || 'null');
    globalState.company.data.set(company);
    globalState.company.lastUpdated.set(new Date());
  },

  setCompanyLoading: (isLoading: boolean) => {
    //stateLogger.debug(`Definindo loading da empresa: ${isLoading}`);
    globalState.company.isLoading.set(isLoading);
  },

  setCompanyError: (error: string | null) => {
    if (error) {
      stateLogger.error('Erro no estado da empresa:', error);
    }
    globalState.company.error.set(error);
  },

  clearCompanyData: () => {
    stateLogger.debug('Limpando dados da empresa');
    globalState.company.data.set(null);
    globalState.company.error.set(null);
    globalState.company.lastUpdated.set(null);
  },
};

export default globalState; 
